import type { LucideIcon } from 'lucide-react';
import { CLIENT_USER_TYPES, PLATFORM_USER_TYPES, type AuthUser, type UserType } from '@/types/auth';
import { canAccess } from '@/utils/permissions';
import { MASTER_SCREENS, MASTERS_GROUP_ICON, SCREENS, type Screen } from './screens';

export interface NavLink {
  kind: 'link';
  to: string;
  label: string;
  icon: LucideIcon;
}

export interface NavGroup {
  kind: 'group';
  label: string;
  icon: LucideIcon;
  children: NavLink[];
}

export type NavItem = NavLink | NavGroup;

type NavEntry =
  | Screen
  | { group: string; icon: LucideIcon; screens: readonly Screen[] }
  /** A screen both portals use, placed where AP's sidebar had it for these user types. */
  | { screen: Screen; userTypes: readonly UserType[] };

/**
 * Sidebar order. Each screen's access rule names its portal's user types, so a Super Admin sees
 * the platform screens and a Client Admin the client screens — never both.
 */
const SIDEBAR: readonly NavEntry[] = [
  SCREENS.dashboard,
  // Super Admin portal
  SCREENS.adminUsers,
  SCREENS.clientUsers,
  { screen: SCREENS.farmers, userTypes: PLATFORM_USER_TYPES },
  { screen: SCREENS.animals, userTypes: PLATFORM_USER_TYPES },
  { screen: SCREENS.grievances, userTypes: PLATFORM_USER_TYPES },
  SCREENS.loginLogs,
  SCREENS.mlLogs,
  SCREENS.notifications,
  SCREENS.faqs,
  { group: 'Masters', icon: MASTERS_GROUP_ICON, screens: MASTER_SCREENS },
  SCREENS.grievanceArchive,
  // Client portal
  SCREENS.administrators,
  SCREENS.agents,
  { screen: SCREENS.farmers, userTypes: CLIENT_USER_TYPES },
  { screen: SCREENS.animals, userTypes: CLIENT_USER_TYPES },
  { screen: SCREENS.grievances, userTypes: CLIENT_USER_TYPES },
];

const toLink = (s: Screen): NavLink => ({ kind: 'link', to: s.path, label: s.label, icon: s.icon });

/** The sidebar for this user: hidden screens are dropped, and empty groups with them. */
export function buildNavigation(user: AuthUser | null): NavItem[] {
  const items: NavItem[] = [];
  for (const entry of SIDEBAR) {
    if ('screen' in entry) {
      if (user && entry.userTypes.includes(user.userType) && canAccess(user, entry.screen.access))
        items.push(toLink(entry.screen));
    } else if ('group' in entry) {
      const children = entry.screens.filter((s) => canAccess(user, s.access)).map(toLink);
      if (children.length > 0)
        items.push({ kind: 'group', label: entry.group, icon: entry.icon, children });
    } else if (canAccess(user, entry.access)) {
      items.push(toLink(entry));
    }
  }
  return items;
}
